import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { debounceTime, map, Observable, startWith } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  ChatService,
  ChatSettingStyles,
  DEFAULT_CHAT_STYLES,
} from '../../../services/chat.service';
import { TutorService } from '../../../services/tutor.service';
import { FONT_LIST } from '../../../helpers/font-list';
import {
  AID_LOADER_STYLES,
  AID_STYLE_FIELDS,
  AidContrastIssue,
  aidContrastIssues,
  DEFAULT_AID_LOADER,
  resolveAidStyle,
  ResolvedAidStyle,
} from '../../../pages/chat-styling/aid-style';
import { svgMaskUrl } from '../../../pages/chat-styling/icon-mask';
export interface ChatSettingInput {
  type:
    | 'text' | 'color' | 'select' | 'boolean' | 'multi-select' | 'number' | 'image-upload' | 'autocomplete' | 'range'
    /** Farbe der Lernhilfen: leer = „Automatisch“ (abgeleitet). */
    | 'aid-color'
    /** „Interface“ (Classic/Liquid) + bei Liquid „Dot Type“ — ein
     *  gespeicherter Wert `widgetDesign` (window | classic | liquid). */
    | 'widget-design';
  name: string;
  label: string;
  value: string | boolean | number;
  options?: { label: string; value: string }[];
  autocompleteOptions?: string[];
  placeholder?: string;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Reiter der Chat-Styling-Seite, in dem das Feld steht: „window“
   *  (Chat-Fenster, Standard) oder „bubble“ (Chat-Blase). */
  section?: 'window' | 'bubble' | 'aids' | 'all' | 'hidden';
  /** Für welches „Interface“ das Feld gilt (fehlt = beide). Classic = das
   *  bisherige Fenster, Liquid = neues Design „Orbi Command“. */
  interfaces?: Array<'classic' | 'liquid'>;
  /** Abweichende Beschriftung / Erklärung bei Liquid. */
  liquidLabel?: string;
  liquidDescription?: string;
  /** Größter Wert eines Schiebereglers bei Liquid (z. B. Logo im Canvas-Kopf). */
  liquidMax?: number;
  /** Hinweis unter einem Upload (z. B. erlaubte Dateiformate). */
  hint?: string;
  liquidHint?: string;
  /** Auswahl bei Liquid (andere Reihenfolge/Beschriftung). */
  liquidOptions?: { label: string; value: string }[];
  /** Bei Liquid zuerst im Reiter. */
  liquidFirst?: boolean;
  /** Bei Liquid nur sichtbar, wenn die Bedingung auf die Formularwerte passt. */
  liquidShowIf?: (value: Record<string, any>) => boolean;
  /** Einheit hinter dem Wert eines Schiebereglers (`range`), Standard 'px'. */
  unit?: string;
  /** Farbfeld darf einen Verlauf tragen (Reiter „Verlauf“ in der
   *  Farbauswahl). Nur für Flächen — Schriftfarben bleiben einfarbig. */
  allowGradient?: boolean;
}

@Component({
  selector: 'app-chat-settings-form',
  templateUrl: './chat-settings-form.component.html',
  styleUrl: './chat-settings-form.component.scss',
})
export class ChatSettingsFormComponent implements OnInit {
  @Input() tutorId!: string;
  @Input() chatSettingsId?: string;
  /** Zeigt nur an, ändert nichts: jede Wertänderung wird sofort auf den
   *  angezeigten Stand zurückgesetzt und als `lockedEdit` gemeldet. Die
   *  Chat-Styling-Seite sperrt so das Standard-Design, solange es kein
   *  eigenes Styling gibt — als zweite Linie hinter ihrer Ereignissperre,
   *  für Wege, die an ihr vorbeigehen (Schriftliste im Overlay, Touch am
   *  Schieberegler, IME). */
  @Input() locked = false;
  @Output() stylesChange = new EventEmitter<Record<string, any>>();
  @Output() lockedEdit = new EventEmitter<void>();

  chatSettings: ChatSettingInput[] = [];
  /** Welcher Reiter rechts gewählt ist — die Karte zeigt nur seine Felder. */
  @Input() section: 'window' | 'bubble' | 'aids' = 'window';
  /** Felder des gewählten Reiters; neu gebaut nur, wenn `section` wechselt. */
  visibleSettings: ChatSettingInput[] = [];

  form!: FormGroup;

  /**
   * Lernhilfen („automatisch, bis überschrieben“): das echte Formularfeld eines
   * Stil-Bausteins ist `null`, solange er automatisch ist — so wird nichts
   * gespeichert und er folgt Primär-/Sekundärfarbe. Angezeigt wird ein
   * Stellvertreter-Feld mit dem abgeleiteten Wert; erst eine Eingabe dort
   * schreibt ins echte Feld. „Automatisch“ setzt es wieder auf `null`.
   */
  aidProxy: Record<string, FormControl> = {};
  aidResolved!: ResolvedAidStyle;
  /** Kontrastwarnungen je Baustein (nur gesetzte Werte). */
  aidIssues: Record<string, AidContrastIssue> = {};
  private syncingAidProxy = false;

  filteredOptionsMap: Record<string, Observable<string[]>> = {};

  private isApplyingSettings = false;

  constructor(
    private fb: FormBuilder,
    private chatService: ChatService,
    private tutorService: TutorService,
    private translate: TranslateService
  ) {
    this.chatSettings = this.buildChatSettings();
    this.updateVisibleSettings();
    const form = this.chatSettings?.reduce((acc: any, setting) => {
      acc[setting.name] = [setting.value];
      return acc;
    }, {});

    this.form = this.fb.group(form);
    this.buildAidProxies();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['section']) this.updateVisibleSettings();
    const change = changes['chatSettingsId'];
    if (!change) return;
    // Auch ohne Id (das letzte Styling wurde gelöscht): zurück auf das
    // Standard-Design, statt die Werte des gelöschten weiter zu zeigen — die
    // Chat-Styling-Seite zeigt das Formular dann weiter an.
    if (change.currentValue || !change.firstChange) {
      this.updateChatSettings(change.currentValue);
    }
  }

  /** Zuletzt angewendeter Stand — dahin setzt `locked` zurück. */
  private appliedValue: Record<string, any> = {};
  private reverting = false;
  /** Theme-Farben des zuletzt gesehenen Stands — Bezug für `followTheme`. */
  private themeBase: Record<string, any> = {};

  updateChatSettings(id?: string) {
    const settings = this.getChatSetting(id);
    const saved: Partial<ChatSettingStyles> = settings?.styles ?? {};
    const merged = {
      ...this.defaultChatSettings,
      // Stylings von vor den Pop-up-Feldern: Hintergrund und Schrift der
      // Pop-up-Nachricht folgen ihren eigenen Farben — so zeigt das Widget
      // sie, solange die Schlüssel fehlen. Sonst schriebe die nächste
      // Änderung (das Formular speichert alle Werte) die Standardfarben fest.
      ...(saved.secondaryColor ? { popupBackgroundColor: saved.secondaryColor } : {}),
      ...(saved.botTextColor ? { popupTextColor: saved.botTextColor } : {}),
      ...saved,
    };
    this.appliedValue = merged;
    this.themeBase = merged;
    this.isApplyingSettings = true;
    this.form.patchValue(merged, { emitEvent: false });
    this.updateVisibleSettings();
    this.checkIconSvg();
    this.refreshAidProxies();
    // Allow native range-input events to fire and be ignored before lifting the guard.
    // 600 ms > debounceTime(500) so any queued emission is also suppressed.
    setTimeout(() => { this.isApplyingSettings = false; }, 600);
    this.stylesChange.emit(merged);
  }

  get defaultChatSettings(): any {
    // return objects with default values
    return this.chatSettings.reduce((acc: { [key: string]: any }, setting) => {
      acc[setting.name] = setting.value;
      return acc;
    }, []);
  }

  private updateVisibleSettings() {
    const iface = this.currentInterface;
    const value = this.form?.getRawValue() ?? {};
    const visible = this.chatSettings.filter(
      (s) =>
        (s.section === 'all' || (s.section ?? 'window') === this.section) &&
        (!s.interfaces || s.interfaces.includes(iface)) &&
        (iface !== 'liquid' || !s.liquidShowIf || s.liquidShowIf(value))
    );
    this.visibleSettings =
      iface === 'liquid'
        ? [...visible.filter((s) => s.liquidFirst), ...visible.filter((s) => !s.liquidFirst)]
        : visible;
  }

  optionsOf(setting: ChatSettingInput): { label: string; value: string }[] {
    return this.currentInterface === 'liquid' && setting.liquidOptions ? setting.liquidOptions : setting.options ?? [];
  }

  /** Gewähltes Interface; ohne Wahl gelten die Felder von Classic. */
  get currentInterface(): 'classic' | 'liquid' {
    return this.interfaceOf(this.form?.get('widgetDesign')?.value) || 'classic';
  }

  hintOf(setting: ChatSettingInput): string | undefined {
    return this.currentInterface === 'liquid' && setting.liquidHint ? setting.liquidHint : setting.hint;
  }

  /** Ist das eigene Icon ein SVG? Nur dann gibt es die Icon-Farbe (Liquid).
   *  Hochgeladene Icons haben keine Endung — einmal laden und nachsehen. */
  iconIsSvg = false;
  private iconChecked: string | null = null;

  private checkIconSvg(): void {
    const url = this.form?.get('chatBubbleIcon')?.value || null;
    if (url === this.iconChecked) return;
    this.iconChecked = url;
    this.iconIsSvg = false;
    this.updateVisibleSettings();
    if (!url) return;
    svgMaskUrl(url).then((mask) => {
      if (this.iconChecked !== url) return;
      this.iconIsSvg = !!mask;
      this.updateVisibleSettings();
    });
  }

  labelOf(setting: ChatSettingInput): string {
    return this.currentInterface === 'liquid' && setting.liquidLabel ? setting.liquidLabel : setting.label;
  }

  descriptionOf(setting: ChatSettingInput): string | undefined {
    return this.currentInterface === 'liquid' && setting.liquidDescription
      ? setting.liquidDescription
      : setting.description;
  }

  rangeMax(setting: ChatSettingInput): number {
    return this.currentInterface === 'liquid' && setting.liquidMax ? setting.liquidMax : setting.max ?? 56;
  }

  control(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  /** Interface-Typ: 'classic' = Chat (bisheriges Fenster), 'liquid' = neues
   *  Design ('classic' war ein früherer Dot Type davon), '' = noch nicht
   *  gewählt (je Agent automatisch). */
  interfaceOf(value: string): '' | 'classic' | 'liquid' {
    if (value === 'window') return 'classic';
    return value === 'classic' || value === 'liquid' ? 'liquid' : '';
  }

  setInterface(kind: 'classic' | 'liquid'): void {
    const ctrl = this.control('widgetDesign');
    if (this.interfaceOf(ctrl.value) === kind && ctrl.value !== 'classic') return;
    this.swapDefaultPlaceholder(kind);
    ctrl.setValue(kind === 'classic' ? 'window' : 'liquid');
    ctrl.markAsDirty();
    this.updateVisibleSettings();
  }

  /** Eingabeplatzhalter: Liquid hat einen eigenen Standard („Sag mir, was du
   *  brauchst.“). Steht noch der Standard des anderen Interface im Feld (nicht
   *  selbst geändert), wird er beim Umschalten mitgetauscht. */
  private swapDefaultPlaceholder(kind: 'classic' | 'liquid'): void {
    const ctrl = this.form.get('inputPlaceholder');
    if (!ctrl) return;
    const chatDefault = this.translate.instant('pages.chatStyling.form.inputPlaceholder.default');
    const liquidDefault = this.translate.instant('pages.chatStyling.form.inputPlaceholder.liquidDefault');
    const current = (ctrl.value ?? '').trim();
    if (kind === 'liquid' && (!current || current === chatDefault)) ctrl.setValue(liquidDefault);
    if (kind === 'classic' && current === liquidDefault) ctrl.setValue(chatDefault);
  }


  getChatSetting(id?: string) {
    return this.chatService.chatSettings$.value.find((c) => c.id === id);
  }

  getThrottleTime() {
    return 500;
  }

  async ngOnInit() {
    this.chatSettings
      .filter(s => s.type === 'autocomplete' && s.autocompleteOptions)
      .forEach(setting => {
        this.filteredOptionsMap[setting.name] = this.form.get(setting.name)!.valueChanges.pipe(
          startWith(this.form.get(setting.name)!.value || ''),
          map(value => this.filterAutocomplete(value, setting.autocompleteOptions!))
        );
      });

    this.appliedValue = { ...this.form.value };
    this.themeBase = this.appliedValue;
    this.form.valueChanges.subscribe(() => {
      if (!this.locked || this.reverting) return;
      // Nebulars Autocomplete ruft in writeValue selbst wieder onChange auf —
      // ohne die Sperre setzte das Zurücksetzen sich endlos neu in Gang.
      this.reverting = true;
      this.form.patchValue(this.appliedValue, { emitEvent: false });
      this.reverting = false;
      this.updateVisibleSettings();
      // Zurückgesetzt wird immer, gemeldet nur echte Eingaben: während
      // updateChatSettings anwendet, kommen die Echos des eigenen patchValue.
      if (!this.isApplyingSettings) this.lockedEdit.emit();
    });

    this.form.valueChanges
      .pipe(debounceTime(this.getThrottleTime()))
      .subscribe((value) => {
        if (this.isApplyingSettings || this.locked) return;
        this.stylesChange.emit(value);
        // Ohne Styling zeigt das Formular das Standard-Design — das wird nie
        // gespeichert (die Chat-Styling-Seite sperrt es ohnehin).
        if (!this.chatSettingsId) return;
        const settings = this.getChatSetting(this.chatSettingsId);

        this.chatService
          .updateChatSettings({
            ...settings!,
            styles: value,
          })
          .subscribe({
            next: () => {},
            error: (err) => console.error('Failed to save chat settings:', err),
          });
      });

    // Stellvertreter der Lernhilfen-Felder zeigen immer den aktuellen Stand —
    // abgeleitete Werte ändern sich mit Primär-/Sekundärfarbe live mit.
    this.form.valueChanges.subscribe(() => this.refreshAidProxies());
    // Liquid: Icon-Felder erscheinen nur bei Widget Trigger Dot/Blob, die
    // Icon-Farbe nur ohne Icon oder mit SVG.
    this.form.get('chatBubbleStyle')?.valueChanges.subscribe(() => this.updateVisibleSettings());
    this.form.get('chatBubbleIcon')?.valueChanges.subscribe(() => this.checkIconSvg());

    // Als letztes abonniert: das patchValue in followTheme sendet verschachtelt
    // — so bekommt die Speicher-Kette den neuen Stand NACH dem alten und
    // speichert ihn (debounce nimmt den jüngsten).
    this.form.valueChanges.subscribe((value) => {
      if (this.locked || this.isApplyingSettings) return;
      this.followTheme(value);
    });
  }

  /** Field catalogue. Starting values come from `DEFAULT_CHAT_STYLES` (the
   *  one default styling — also the example widget on the empty
   *  Chat-Styling page); change the look there, not here. */
  private buildChatSettings(): ChatSettingInput[] {
    return [
      // Aufbau des Widgets. Bewusst ohne Wert in DEFAULT_CHAT_STYLES: ein
      // Styling ohne Wahl ('') speichert '' und lässt jeden Agent bei seinem
      // Stand (bisherige Agents Fenster, neue Orbi Command) — ein Standard
      // hier würde beim nächsten Speichern alle zugeordneten Agents umstellen.
      {
        name: 'widgetDesign',
        type: 'widget-design',
        // Kein Feld im Formular: der Schalter „Interface-Typ“ steht oben auf
        // der Seite (chat-styling.component › onInterfaceType → setInterface).
        // Das Steuerelement bleibt hier, damit es mit allen Feldern speichert.
        section: 'hidden',
        label: this.translate.instant('pages.chatStyling.interfaceType.label'),
        value: '',
        description: this.translate.instant('pages.chatStyling.interfaceType.hint.auto'),
      },
      {
        name: 'logo',
        liquidDescription: this.translate.instant('pages.chatStyling.form.logo.liquidDescription'),
        type: 'image-upload',
        label: this.translate.instant('pages.chatStyling.form.logo.label'),
        value: DEFAULT_CHAT_STYLES.logo,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.logo.placeholder'
        ),
        description: this.translate.instant(
          'pages.chatStyling.form.logo.description'
        ),
      },
      {
        name: 'logoSize',
        liquidMax: 28,
        type: 'range',
        label: this.translate.instant('pages.chatStyling.form.logoSize.label'),
        value: DEFAULT_CHAT_STYLES.logoSize,
        min: 16,
        // Capped at the chat-widget's actual `max-height: 42px` on .logo
        // (chat-action-bar.component.scss). Anything above is silently
        // clipped, so we don't expose dead slider range.
        max: 42,
        step: 2,
      },
      {
        name: 'inputPlaceholder',
        type: 'text',
        label: this.translate.instant(
          'pages.chatStyling.form.inputPlaceholder.label'
        ),
        value: this.translate.instant(
          'pages.chatStyling.form.inputPlaceholder.default'
        ),
        placeholder: this.translate.instant(
          'pages.chatStyling.form.inputPlaceholder.placeholder'
        ),
        description: this.translate.instant(
          'pages.chatStyling.form.inputPlaceholder.description'
        ),
      },
      {
        name: 'primaryColor',
        allowGradient: true,
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.primaryColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.primaryColor,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.primaryColor.placeholder'
        ),
        description: this.translate.instant(
          'pages.chatStyling.form.primaryColor.description'
        ),
      },
      // Liquid: alles auf der Primärfarbe (Apps, Vorschläge, Senden) — bei
      // heller Primärfarbe braucht es dunkle Symbole (Marcel, 29.10.2026).
      {
        name: 'appIconColor',
        interfaces: ['liquid'],
        type: 'color',
        label: this.translate.instant('pages.chatStyling.form.appIconColor.label'),
        value: '#ffffff',
        description: this.translate.instant('pages.chatStyling.form.appIconColor.description'),
      },
      {
        name: 'secondaryColor',
        allowGradient: true,
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.secondaryColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.secondaryColor,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.secondaryColor.placeholder'
        ),
        description: this.translate.instant(
          'pages.chatStyling.form.secondaryColor.description'
        ),
      },
      {
        name: 'botTextColor',
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.botTextColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.botTextColor,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.botTextColor.placeholder'
        ),
      },
      {
        name: 'userTextColor',
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.userTextColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.userTextColor,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.userTextColor.placeholder'
        ),
      },
      {
        name: 'userBubbleColor',
        allowGradient: true,
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.userBubbleColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.userBubbleColor!,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.userBubbleColor.placeholder'
        ),
      },
      {
        name: 'backgroundColor',
        allowGradient: true,
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.backgroundColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.backgroundColor,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.backgroundColor.placeholder'
        ),
      },
      {
        name: 'fontFamily',
        type: 'autocomplete',
        autocompleteOptions: FONT_LIST,
        label: this.translate.instant(
          'pages.chatStyling.form.fontFamily.label'
        ),
        value: DEFAULT_CHAT_STYLES.fontFamily,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.fontFamily.placeholder'
        ),
        description: this.translate.instant(
          'pages.chatStyling.form.fontFamily.description'
        ),
      },
      {
        name: 'chatBubbleIcon',
        liquidLabel: this.translate.instant('pages.chatStyling.form.chatBubbleIcon.liquidLabel'),
        liquidDescription: this.translate.instant('pages.chatStyling.form.chatBubbleIcon.liquidDescription'),
        // Liquid: nur für Dot und Blob (Orbi bleibt Orbi); ohne Icon KI-Sterne
        liquidShowIf: (v) => v['chatBubbleStyle'] === 'dot' || v['chatBubbleStyle'] === 'blob',
        hint: this.translate.instant('pages.chatStyling.form.chatBubbleIcon.formats'),
        liquidHint: this.translate.instant('pages.chatStyling.form.chatBubbleIcon.formatsLiquid'),
        section: 'bubble',
        type: 'image-upload',
        label: this.translate.instant(
          'pages.chatStyling.form.chatBubbleIcon.label'
        ),
        value: DEFAULT_CHAT_STYLES.chatBubbleIcon,
        placeholder: this.translate.instant(
          'pages.chatStyling.form.chatBubbleIcon.placeholder'
        ),
        description: this.translate.instant(
          'pages.chatStyling.form.chatBubbleIcon.description'
        ),
      },
      {
        name: 'chatBubbleIconSize',
        // Liquid: Größe des Icons bzw. der KI-Sterne im Dot/Blob (Kreis ~58px)
        liquidShowIf: (v) => v['chatBubbleStyle'] === 'dot' || v['chatBubbleStyle'] === 'blob',
        liquidMax: 44,
        section: 'bubble',
        type: 'range',
        label: this.translate.instant(
          'pages.chatStyling.form.chatBubbleIconSize.label'
        ),
        value: DEFAULT_CHAT_STYLES.chatBubbleIconSize,
        min: 16,
        max: 56,
        step: 2,
      },
      // Liquid: Farbe der KI-Sterne bzw. eines SVG-Icons im Dot/Blob. Das
      // Widget färbt per CSS-Maske — Bilder (PNG, JPG) behalten ihre Farben,
      // deshalb nur ohne Icon oder mit SVG.
      {
        name: 'chatBubbleIconColor',
        interfaces: ['liquid'],
        liquidShowIf: (v) =>
          (v['chatBubbleStyle'] === 'dot' || v['chatBubbleStyle'] === 'blob') &&
          (!v['chatBubbleIcon'] || this.iconIsSvg),
        section: 'bubble',
        type: 'color',
        label: this.translate.instant('pages.chatStyling.form.chatBubbleIconColor.label'),
        value: '#ffffff',
        description: this.translate.instant('pages.chatStyling.form.chatBubbleIconColor.description'),
      },
      {
        name: 'chatBubbleStyle',
        // Liquid: „Widget Trigger“ links in der Leiste (Orbi | Dot | Blob) —
        // derselbe Wert wie die Chat-Blase im Chat-Interface.
        liquidLabel: this.translate.instant('pages.chatStyling.form.chatBubbleStyle.liquidLabel'),
        liquidDescription: this.translate.instant('pages.chatStyling.form.chatBubbleStyle.liquidDescription'),
        liquidFirst: true,
        liquidOptions: (['glass', 'dot', 'blob'] as const).map((value) => ({
          value,
          label: this.translate.instant(`pages.chatStyling.form.chatBubbleStyle.liquidOptions.${value}`),
        })),
        section: 'bubble',
        type: 'select',
        label: this.translate.instant(
          'pages.chatStyling.form.chatBubbleStyle.label'
        ),
        value: DEFAULT_CHAT_STYLES.chatBubbleStyle,
        description: this.translate.instant(
          'pages.chatStyling.form.chatBubbleStyle.description'
        ),
        options: [
          {
            value: 'dot',
            label: this.translate.instant(
              'pages.chatStyling.form.chatBubbleStyle.options.dot'
            ),
          },
          {
            value: 'blob',
            label: this.translate.instant(
              'pages.chatStyling.form.chatBubbleStyle.options.blob'
            ),
          },
          {
            value: 'glass',
            label: this.translate.instant(
              'pages.chatStyling.form.chatBubbleStyle.options.glass'
            ),
          },
        ],
      },
      // Pop-up-Nachricht über der geschlossenen Chat-Blase (proaktive Frage
      // mit Antwort-Buttons). Transparenz nur für den Hintergrund, die
      // Schrift bleibt deckend. Alte Stylings ohne diese Werte: siehe
      // updateChatSettings.
      {
        name: 'popupBackgroundColor',
        interfaces: ['classic'],
        allowGradient: true,
        section: 'bubble',
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.popupBackgroundColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.popupBackgroundColor,
        description: this.translate.instant(
          'pages.chatStyling.form.popupBackgroundColor.description'
        ),
      },
      {
        name: 'popupTransparency',
        interfaces: ['classic'],
        section: 'bubble',
        type: 'range',
        label: this.translate.instant(
          'pages.chatStyling.form.popupTransparency.label'
        ),
        value: DEFAULT_CHAT_STYLES.popupTransparency,
        min: 0,
        max: 100,
        step: 5,
        unit: '%',
      },
      {
        name: 'popupTextColor',
        interfaces: ['classic'],
        section: 'bubble',
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.popupTextColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.popupTextColor,
      },
      {
        name: 'popupReplyBackgroundColor',
        interfaces: ['classic'],
        allowGradient: true,
        section: 'bubble',
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.popupReplyBackgroundColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.popupReplyBackgroundColor,
      },
      {
        name: 'popupReplyTransparency',
        interfaces: ['classic'],
        section: 'bubble',
        type: 'range',
        label: this.translate.instant(
          'pages.chatStyling.form.popupReplyTransparency.label'
        ),
        value: DEFAULT_CHAT_STYLES.popupReplyTransparency,
        min: 0,
        max: 100,
        step: 5,
        unit: '%',
      },
      {
        name: 'popupReplyTextColor',
        interfaces: ['classic'],
        section: 'bubble',
        type: 'color',
        label: this.translate.instant(
          'pages.chatStyling.form.popupReplyTextColor.label'
        ),
        value: DEFAULT_CHAT_STYLES.popupReplyTextColor,
      },
      ...this.buildAidSettings(),
      {
        name: 'alphabeesBranding',
        type: 'boolean',
        label: this.translate.instant(
          'pages.chatStyling.form.alphabeesBranding.label'
        ),
        value: DEFAULT_CHAT_STYLES.alphabeesBranding,
        description: this.translate.instant(
          'pages.chatStyling.form.alphabeesBranding.description'
        ),
      },
    ];
  }

  /** Hintergrund und Schrift der Pop-up-Nachricht folgen Sekundärfarbe und
   *  Bot-Schrift, solange der Admin sie nicht selbst anders gesetzt hat
   *  (= gleicher Wert wie die Theme-Farbe vorher). Das Formular speichert
   *  alle Werte — ohne das Mitziehen stünde nach einer Theme-Änderung die
   *  alte Farbe fest im Pop-up. */
  private followTheme(value: Record<string, any>) {
    const pairs: Array<[string, string]> = [
      ['secondaryColor', 'popupBackgroundColor'],
      ['botTextColor', 'popupTextColor'],
    ];
    const same = (a: unknown, b: unknown) =>
      String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase();
    const patch: Record<string, any> = {};
    for (const [theme, popup] of pairs) {
      const previous = this.themeBase[theme];
      if (!same(value[theme], previous) && same(value[popup], previous)) {
        patch[popup] = value[theme];
      }
    }
    // Vor dem patchValue setzen: dessen Echo kommt hier synchron wieder an.
    this.themeBase = value;
    if (Object.keys(patch).length) this.form.patchValue(patch);
  }

  // ------------------------------------------------------------ Lernhilfen

  /** Felder des Reiters „Lernhilfen“: Ladevariante, dann die drei Farben
   *  (Hintergrund, Text, Highlight — AID_STYLE_FIELDS). Alle übrigen Töne
   *  rechnet `resolveAidStyle` daraus; sie sind nicht einstellbar. */
  private buildAidSettings(): ChatSettingInput[] {
    const t = (k: string) => this.translate.instant(`pages.chatStyling.form.aid.${k}`);
    return [
      {
        name: 'aidLoader',
        section: 'aids',
        type: 'select',
        label: t('aidLoader.label'),
        description: t('aidLoader.description'),
        value: DEFAULT_AID_LOADER,
        options: AID_LOADER_STYLES.map((v) => ({ value: v, label: t(`aidLoader.${v}`) })),
      },
      ...AID_STYLE_FIELDS.map((f): ChatSettingInput => ({
        name: f.key,
        section: 'aids',
        type: 'aid-color',
        allowGradient: f.kind === 'fill',
        label: t(`${f.key}.label`),
        description: t(`${f.key}.description`),
        value: null as unknown as string,
      })),
    ];
  }

  private buildAidProxies(): void {
    for (const f of AID_STYLE_FIELDS) {
      const proxy = new FormControl<string | number | null>(null);
      proxy.valueChanges.subscribe((v) => {
        if (this.syncingAidProxy) return;
        const shown = this.aidResolved?.values[f.key];
        if (v === null || v === undefined || v === '') return;
        if (String(v).toLowerCase() === String(shown).toLowerCase()) return;
        this.control(f.key).setValue(v);
      });
      this.aidProxy[f.key] = proxy;
    }
    this.refreshAidProxies();
  }

  /** Stellvertreter auf den aktuellen Stand bringen (ohne Rückmeldung). */
  private refreshAidProxies(): void {
    if (!this.form) return;
    this.aidResolved = resolveAidStyle(this.form.getRawValue());
    this.aidIssues = Object.fromEntries(aidContrastIssues(this.aidResolved).map((i) => [i.key, i]));
    this.syncingAidProxy = true;
    for (const f of AID_STYLE_FIELDS) {
      this.aidProxy[f.key]?.setValue(this.aidResolved.values[f.key], { emitEvent: true });
    }
    this.syncingAidProxy = false;
  }

  isAidAuto(key: string): boolean {
    const v = this.form.get(key)?.value;
    return v === null || v === undefined || v === '';
  }

  /** Zurück auf „Automatisch“ — der Wert folgt wieder dem Standard bzw. der Primärfarbe. */
  resetAid(key: string): void {
    this.control(key).setValue(null);
  }

  aidIssueText(key: string): string | null {
    const issue = this.aidIssues[key];
    if (!issue) return null;
    return this.translate.instant('pages.chatStyling.form.aid.contrastWarning', {
      ratio: issue.ratio.toLocaleString(this.translate.currentLang || 'de'),
      min: issue.min.toLocaleString(this.translate.currentLang || 'de'),
      on: this.translate.instant(`pages.chatStyling.form.aid.${issue.on}.label`),
    });
  }

  private filterAutocomplete(value: string, options: string[]): string[] {
    const filterValue = (value || '').toLowerCase();
    return options.filter(option => option.toLowerCase().includes(filterValue));
  }

}
